
import useAxios from '../../../hooks/useAxios';
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Button,
    Checkbox,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    TextField,
    IconButton,
    InputAdornment,
    Collapse,
    TableFooter
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Search, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { formatValueTo2Decimal } from "../../../utils/Common/index";

const getPNLColor = (pnl) => (pnl >= 0 ? "green" : "red");

const OverdueStocks = ({ holdings, dueHoldings = [] }) => {

    const updatedHoldings = holdings.map((stock) => ({
        ...stock,
        pnl: stock.current_value - stock.book_value,
    }));

    const getInvestmentReviewURL = `${process.env.REACT_APP_APP_TRADE_URL}/data/investment-preview`;




    const [selectedRows, setSelectedRows] = useState([]);
    const [orderMessage, setOrderMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [loading, setLoading] = useState(false);
    const [soldStocks, setSoldStocks] = useState([]);
    const [failedStockOrders, setFailedStockOrders] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [quantityMap, setQuantityMap] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleRows, setVisibleRows] = useState(20);
    const [openRows, setOpenRows] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(false);


    const token = useSelector((state) => state.user_authentication.token);
    const placeBulkOrderURL = `${process.env.REACT_APP_APP_TRADE_URL}/trade/place`;

    const { data: placeBulkOrder, error: errorBulkOrder, loading: loadingBulkOrder, callAPI: callBulkOrderAPI } = useAxios();


    const getStrategy = (row) => {
        if (row["momentum condition"]) return 'Momentum';
        if (row["value_satisfaction"]) return 'Value';
        return 'Regular';
    };


    const currentData = updatedHoldings
        .filter(row => row.symbol.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => {
            if (!sortConfig.key) return 0;
            const valA = a[sortConfig.key];
            const valB = b[sortConfig.key];
            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        })
        .slice(0, visibleRows);
    console.log("currentdata===", currentData);

    useEffect(() => {
        setOpenRows((prev) => {
            if (prev.length !== currentData.length) {
                return currentData.map(() => false);
            }
            return prev;
        });
    }, [currentData.length]);

    const toggleSelect = (symbol) => {
        setSelectedRows((prev) => {
            const alreadySelected = prev.includes(symbol);
            const updated = alreadySelected ? prev.filter((s) => s !== symbol) : [...prev, symbol];

            setQuantityMap((prevMap) => ({
                ...prevMap,
                [symbol]: alreadySelected ? '' : 0,
            }));

            return updated;
        });
    };

    const toggleRow = (index) => {
        setOpenRows((prev) =>
            prev.map((open, i) => (i === index ? !open : open))
        );
    };

    const handleQuantityChange = (symbol, value) => {
        const numericValue = Number(value);
        setQuantityMap((prev) => ({
            ...prev,
            [symbol]: isNaN(numericValue) || numericValue < 0 ? '' : numericValue,
        }));
    };

    const generatePreview = () => {

        let hasError = false;
        const stock_payload = [];
        setOrderMessage("");

        selectedRows.forEach((symbol) => {
            const rawQty = quantityMap[symbol];
            const validQty = /^[1-9]\d*$/.test(rawQty); // positive integer, no leading zero

            if (!validQty) {
                hasError = true;
            } else {
                stock_payload.push({ symbol, quantity: Number(rawQty) });
            }
        });

        if (hasError || stock_payload.length === 0) {
            setSeverity('warning');
            setOrderMessage('Please select stocks and enter valid quantities (no 0, no 01, no blank).');
            return;
        }

        const payload = {
            stock_quantities: stock_payload,
            strategy: "REGULAR"
        };

        setLoading(true);
        callInvestReviewAPI(getInvestmentReviewURL, 'POST', payload, {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json'
        });
    };

    const {
        data: getInvestReview,
        error: errorInvestReview,
        loading: loadingInvestReview,
        callAPI: callInvestReviewAPI
    } = useAxios();

    useEffect(() => {


        if (getInvestReview) {

            setLoading(false);
            if (getInvestReview.success === true) {
                console.log(getInvestReview.preview);
                setPreviewData(getInvestReview);
                setShowPreview(true);
            } else {
                setSeverity("warning");
                setOrderMessage("Get price failed. Please try again.");
            }
        } else if (loadingInvestReview && errorInvestReview) {
            setLoading(false);
            console.error(errorInvestReview);
            setSeverity("error");
            setOrderMessage("Failed to get Price. Please try again.");
        }
        setTimeout(() => {
            setSeverity('');
            setOrderMessage('');
        }, 3000);
    }, ([getInvestReview, errorInvestReview, loadingInvestReview]));


    useEffect(() => {
        if (placeBulkOrder) {
            setLoading(false);
            const successfulOrders = placeBulkOrder.results?.filter((item) => item.success) || [];
            const failedOrders = placeBulkOrder.results?.filter((item) => !item.success) || [];

            if (successfulOrders.length > 0) {
                setSeverity('success');
                setSoldStocks(successfulOrders);
                setOrderMessage(`${successfulOrders.map((o) => o.symbol).join(', ')} stock(s) sold successfully.`);
            }

            if (failedOrders.length > 0) {
                setSeverity('warning');
                setFailedStockOrders(failedOrders);
                setOrderMessage(`Failed to sell: ${failedOrders.map((o) => o.symbol).join(', ')}`);
            }

            if (successfulOrders.length === 0 && failedOrders.length === 0) {
                setSeverity('info');
                setOrderMessage('No orders were processed.');
            }

            setSelectedRows([]);
            setQuantityMap({});
            setShowPreview(false);
        } else if (!loadingBulkOrder && errorBulkOrder) {
            setLoading(false);
            setSeverity('error');
            setOrderMessage('Sell request failed. Please try again.');
        }

        setTimeout(() => {
            setSelectedRows([]);
            setQuantityMap({});
            setSeverity('');
            setOrderMessage('');
        }, 3000);
    }, [placeBulkOrder, errorBulkOrder, loadingBulkOrder]);

    const handleSell = () => {
        const dataSource = tabIndex === 0 ? updatedHoldings : dueHoldings;
        const rowMap = new Map(dataSource.map((row) => [row.symbol, row]));

        const payload = selectedRows.map((symbol) => {
            const row = rowMap.get(symbol);
            const qty = Number(quantityMap[symbol]) || row?.quantity || 0;
            return {
                symbol,
                quantity: qty,
                order_type: "SELL",
                strategy: "REGULAR"
            };
        }).filter((item) => item.quantity > 0);

        if (payload.length === 0) {
            setSeverity('warning');
            setOrderMessage('Please select stocks and enter valid quantities.');
            return;
        }

        const requestPayload = { orders: payload };
        setLoading(true);
        callBulkOrderAPI(placeBulkOrderURL, 'POST', requestPayload, {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        });
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#2d4890', mb: 2, padding: "20px" }}>
                📈 Divest Overdue Portfolio Holdings
            </Typography>

            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by Symbol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2, padding: "20px" }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    ),
                }}
            />

            <TableContainer component={Paper} elevation={2} sx={{ mb: 3, pt: 3 }}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#2d4890' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'white' }}>Select</TableCell>
                            <TableCell sx={{ color: 'white' }}>Expand</TableCell>
                            <TableCell sx={{ color: 'white', cursor: 'pointer' }} onClick={() => handleSort('symbol')}>
                                Symbol {sortConfig.key === 'symbol' ? (sortConfig.direction === 'asc' ? <ArrowUpward /> : <ArrowDownward />) : null}
                            </TableCell>
                            <TableCell sx={{ color: 'white' }}>Avaliable Shares</TableCell>
                            <TableCell sx={{ color: 'white' }}>Quantity to Sell</TableCell>
                            <TableCell sx={{ color: 'white' }}>Current Price</TableCell>
                            <TableCell sx={{ color: 'white' }}>PNL Value</TableCell>
                            <TableCell sx={{ color: 'white' }}>Strategy</TableCell>
                            <TableCell sx={{ color: 'white' }}>OverDue by Days</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentData.map((row, index) => {
                            const selected = selectedRows.includes(row.symbol);
                            const quantity = quantityMap[row.symbol] || '';
                            return (
                                <React.Fragment key={row.symbol}>
                                    <TableRow>
                                        <TableCell>
                                            <Checkbox checked={selected} onChange={() => toggleSelect(row.symbol)} />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => toggleRow(index)}>
                                                {openRows[index] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row.symbol}</TableCell>
                                        <TableCell>{row.quantity}</TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                inputProps={{ min: 0, max: row.quantity }}
                                                disabled={!selected}
                                                value={quantity}
                                                onChange={(e) => handleQuantityChange(row.symbol, e.target.value)}
                                                sx={{ width: 100 }}
                                            />
                                        </TableCell>
                                        <TableCell>₹{row.current_value?.toFixed(2)}</TableCell>
                                        <TableCell sx={{ color: getPNLColor(row.pnl) }}>
                                            ₹{row.pnl.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell>{getStrategy(row)}</TableCell>
                                        <TableCell sx={{ color: 'red' }}>{row.overdue_by_days}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ p: 0, backgroundColor: "#f4f7ff" }}>
                                            <Collapse in={openRows[index]} timeout="auto" unmountOnExit>
                                                <Box sx={{ m: 1 }}>
                                                    <Typography>
                                                        Current Price: <strong>₹{row.current_value?.toFixed(2)}</strong>
                                                    </Typography>
                                                    <Typography>
                                                        PNL Value: <strong style={{ color: getPNLColor(row.pnl) }}>₹{row.pnl}</strong>
                                                    </Typography>
                                                    <Typography>
                                                        Shares Available: <strong>{row.quantity}</strong>
                                                    </Typography>
                                                    <Typography>
                                                        Overdue by Days: <strong style={{ color: 'red' }}>{row.overdue_by_days}</strong>
                                                    </Typography>
                                                    <Typography>
                                                        Book Value: <strong>₹{row.book_value?.toFixed(2) || '0.00'}</strong>
                                                    </Typography>
                                                    <Typography>
                                                        Due Date: <strong>{row.due_date}</strong>
                                                    </Typography>
                                                    <Typography>
                                                        Buy Date: <strong>{row.buy_date}</strong>
                                                    </Typography>
                                                    <Typography>
                                                        Strategy: <strong>{getStrategy(row)}</strong>
                                                    </Typography>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>

                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>



            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                    variant="outlined"
                    onClick={() => generatePreview()}
                    disabled={selectedRows.length === 0}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Review Sell Order'}
                </Button>
            </Box>

            {showPreview && previewData && previewData?.preview.length > 0 && (
                <Box sx={{ mb: 4, p: 2, backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#2d4890', fontWeight: 'bold' }}>
                        🧾 Sell Order Preview
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#e0e7ff' }}>
                                <TableCell>Symbol</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Current Price</TableCell>
                                <TableCell>Investment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {previewData.preview.map((stock) => {
                                const quantity = Number(stock.quantity) || 0;
                                const investment = stock.investment || 0;
                                const currentPrice = stock.current_price || 0;
                                return (
                                    <TableRow key={stock.symbol}>
                                        <TableCell>{stock.symbol}</TableCell>
                                        <TableCell>{quantity}</TableCell>
                                        <TableCell>₹ {currentPrice}</TableCell>
                                        <TableCell>₹ {investment}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={6} align="right">
                                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#2d4890" }}>
                                        Total Investment: ₹ {formatValueTo2Decimal(previewData.total_calculated_investment)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: '#2d4890',
                                color: 'white',
                                px: 3,
                                py: 1,
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: '#1a2f60',
                                },
                            }}
                            onClick={handleSell}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Place Bulk Sell Order'}
                        </Button>
                    </Box>
                </Box>
            )}

            {failedStockOrders.length > 0 && <Box
                sx={{
                    mt: 6,
                    p: 3,
                    backgroundColor: "#fafafa",
                    borderRadius: 2,
                    boxShadow: 2,
                    borderLeft: "5px solid #2d4890",
                }}
            >
                <Typography variant="h5" gutterBottom>
                    Unsuccessful Stock Orders
                </Typography>


                <Box component="ul" sx={{ pl: 3, listStyleType: "disc" }}>
                    {failedStockOrders.map((stock) => (
                        <Box component="li" key={stock.symbol} sx={{ mb: 1 }}>
                            <Typography variant="body1">
                                <strong>{stock.symbol}</strong> – Message: {stock.error}
                            </Typography>
                        </Box>
                    ))}
                </Box>

            </Box>}



            {orderMessage && (
                <Alert severity={severity} sx={{ mb: 2 }}>
                    {orderMessage}
                </Alert>
            )}

            {failedStockOrders.length > 0 && <Box
                sx={{
                    mt: 6,
                    p: 3,
                    backgroundColor: "#fafafa",
                    borderRadius: 2,
                    boxShadow: 2,
                    borderLeft: "5px solid #2d4890",
                }}
            >
                <Typography variant="h5" gutterBottom>
                    Unsuccessful Stock Orders
                </Typography>


                <Box component="ul" sx={{ pl: 3, listStyleType: "disc" }}>
                    {failedStockOrders.map((stock) => (
                        <Box component="li" key={stock.symbol} sx={{ mb: 1 }}>
                            <Typography variant="body1">
                                <strong>{stock.symbol}</strong> – Message: {stock.error}
                            </Typography>
                        </Box>
                    ))}
                </Box>

            </Box>}
        </Box>
    );
};

export default OverdueStocks;
